/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom"; // <-- No BrowserRouter here!

import { useAuth, useAuthUpdate, User } from "contexts/AuthContext";
import { jwtDecode } from "jwt-decode";
import AccusedRequests from "pages/accusedRequests/AccusedRequests";
import SingleAccused from "pages/accusedRequests/SingleAccused";
import AdvocateAccusedRequests from "pages/advocateAccusedRequests/AdvocateAccusedRequests";
import SingleAdvocateAccused from "pages/advocateAccusedRequests/SingleAdvocateAccused";
import AdvocateAppealRequest from "pages/advocateAppealRequests/AdvocateAppealRequest";
import SingleAdvocateAppeal from "pages/advocateAppealRequests/SingleAdvocateAppeal";
import AdvocateComplaint from "pages/advocateComplaint/AdvocateComplaint";
import NewAdvocateComplaint from "pages/advocateComplaint/NewAdvocateComplaint";
import SingleAdvocateComplaint from "pages/advocateComplaint/SingleAdvocateComplaint";
import AdvocateCourtAppealRequests from "pages/advocateCourtAppealRequests/AdvocateCourtAppealRequests";
import SingleAdvocateCourtAppeal from "pages/advocateCourtAppealRequests/SingleAdvocateCourtAppeal";
import AppealRequest from "pages/appealRequests/AppealRequest";
import SingleAppeal from "pages/appealRequests/SingleAppeal";
import NewComplaint from "pages/complaintRequest/NewComplaint";
import SingleComplaint from "pages/complaintRequest/SingleComplaint";
import CourtAppealRequests from "pages/courtAppealRequests/CourtAppealRequests";
import SingleCourtAppeal from "pages/courtAppealRequests/SingleCourtAppeal";
import ListAdvocateLogs from "pages/logs/ListAdvocateLogs";
import ListProsecutorLogs from "pages/logs/ListProsecutorLogs";
import ReportByMobileRequests from "pages/ReportByMobile/ReportByMobileRequests";
import SingleMobileReuqest from "pages/ReportByMobile/SingleReportMobileRequest";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import ComplaintRequest from "./pages/complaintRequest/ComplaintRequest";
import UserRegister from "./pages/IncidentRegister/userRegister";
import DashboardPage from "pages/dashboard/dashboardPage";
import SessionTimeout from "./components/SessionTimeout";

export interface JwtPayload {
  exp?: number; // Expiration time in seconds
  iat?: number; // Issued at
  sub?: string; // Subject (user id, etc.)
  [key: string]: any; // Any additional claims
}

export function isValidJwtFormat(token: string): boolean {
  if (!token || typeof token !== "string") return false;

  const parts = token.split(".");
  if (parts.length !== 3) return false;

  // Check if all parts are non-empty
  return parts.every((part) => part.trim().length > 0);
}

export function getTokenPayloadSafely(
  token: string | null | undefined,
): JwtPayload | null {
  if (!token || !isValidJwtFormat(token)) {
    // console.warn("Invalid or missing token format");
    return null;
  }

  try {
    return jwtDecode<JwtPayload>(token);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
}

export function decodeAccessToken(
  token: string | null | undefined,
): JwtPayload | null {
  if (!token) return null;

  try {
    return getTokenPayloadSafely(token);
  } catch (err) {
    console.error("Failed to decode access token:", err);

    // Fallback to manual decoding
    try {
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) return null;

      const base64Url = tokenParts[1];
      if (!base64Url) return null;

      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
          .join(""),
      );

      return JSON.parse(jsonPayload) as JwtPayload;
    } catch (fallbackErr) {
      console.error(
        "Failed to decode access token with fallback method:",
        fallbackErr,
      );
      return null;
    }
  }
}

export default function App() {
  const navigate = useNavigate();
  const contextData = useAuth();
  const updateAuthContext = useAuthUpdate();
  // const { user } = useAuth();

  const [internalUserType, setInternalUserType] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tokenLoaded, setTokenLoaded] = useState(false);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // console.log(
      //   "token:",
      //   event.data?.token || event?.data?.data?.access_token
      // );
      // console.log("data", event.data);
      // console.log("event", event);
      if (
        event.data?.type === "SET_SESSION_DATA_FOR_PROSECUTION" ||
        event.data?.type === "SET_TOKEN"
      ) {
        if (event.data.token && isValidJwtFormat(event.data.token)) {
          sessionStorage.setItem(
            "authToken",
            event.data?.token || event?.data?.data?.access_token,
          );
          // console.log("Token received and stored");
          const payload = getTokenPayloadSafely(event.data?.token);
          updateAuthContext({
            ...contextData,
            token: event.data?.token,
            user: {
              id: payload?.user_id?.toString() ?? "",
              user_id: payload?.user_id?.toString() ?? "",
              employee_id: payload?.employee_id?.toString() ?? "",
              admin_unit: payload?.admin_unit?.toString() ?? "",
              org_unit: payload?.org_unit?.toString() ?? "",
              user_type: payload?.user_type?.toString() ?? "external",
              access: event.data.token,
              roles: payload?.roles,
              full_name: payload?.full_name,
              username: payload?.username,
              permissions: payload?.permissions,
              //refresh: response.refresh_token,
            },
          });
          // console.log("Token payload:", payload);
          if (payload?.user_type === "internal") {
            setInternalUserType(true);
          }
          setTokenLoaded(true);
        } else {
          console.error("Invalid token received in SET_TOKEN message");
          setTokenLoaded(true);
        }
      }
      if (event.data?.type === "SET_SESSION_DATA_FOR_STAKEHOLDERS") {
        if (
          event.data.data.access_token &&
          isValidJwtFormat(event.data.data.access_token)
        ) {
          sessionStorage.setItem("authToken", event.data.data.access_token);
          // console.log("Token received and stored");
          const payload = getTokenPayloadSafely(event.data.data.access_token);
          updateAuthContext({
            ...contextData,
            token: event.data.data.access_token,
            user: {
              id: payload?.user_id?.toString() ?? "",
              user_id: payload?.user_id?.toString() ?? "",
              employee_id: payload?.employee_id?.toString() ?? "",
              admin_unit: payload?.admin_unit?.toString() ?? "",
              org_unit: payload?.org_unit?.toString() ?? "",
              user_type: payload?.user_type?.toString() ?? "external",
              access: event.data.access_token,
              roles: payload?.roles,
              full_name: payload?.full_name,
              username: payload?.username,
              permissions: payload?.permissions,
              //refresh: response.refresh_token,
            },
          });
          // console.log("Stakeholder Token payload:", payload);
          setTokenLoaded(true);
        } else {
          console.error("Invalid token received in SET_TOKEN message");
          setTokenLoaded(true);
        }
      }
    }

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Check for existing token on initial load
  useEffect(() => {
    const hasToken =
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("access_token");

    if (hasToken) {
      setIsAuthenticated(true);

      updateAuthContext({
        ...contextData,
        token: hasToken,
        user: getTokenPayloadSafely(hasToken) as User,
      });
    }
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    navigate("/"); // Redirect to main page after login
  };

  const handleLogout = () => {
    sessionStorage.removeItem("authToken");
    setIsAuthenticated(false);
    updateAuthContext({
      ...contextData,
      token: "",
      user: {} as User,
    });
  };

  // const handleLoginSuccess = () => {
  //   setIsAuthenticated(true);
  //   const hasToken = sessionStorage.getItem("authToken");

  //   // const canViewComplaint = [
  //   //   ROLES.INSPECTION_COORDINATOR,
  //   //   ROLES.INSPECTION_PROSECUTOR,
  //   //   ROLES.INSPECTION_REGISTRAR,
  //   //   ROLES.INSPECTION_DIRECTOR,
  //   //   ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  //   //   ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  //   //   ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
  //   // ];

  //   const canViewAdvocate = [
  //     ADVOCATE_ROLES.INSPECTION_COORDINATOR,
  //     ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  //     ADVOCATE_ROLES.INSPECTION_REGISTRAR,
  //     ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  //     ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
  //     ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,

  //     ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
  //     ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
  //     ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  //     ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  //     ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
  //   ];
  //   const parsed = JSON.parse(hasToken || "{}")?.roles;

  //   if (!parsed || parsed.length === 0) navigate("/complaint-request");

  //   const canView = canViewAdvocate?.some((role) => parsed?.includes(role));

  //   navigate(canView ? "/advocate-complaint" : "/complaint-request");
  // };

  // const handleLogout = () => {
  //   sessionStorage.removeItem("authToken");
  //   setIsAuthenticated(false);
  //   updateAuthContext({
  //     ...contextData,
  //     token: "",
  //     user: {} as User,
  //     adminUnitDetail: {} as AdminUnit,
  //   });
  //   navigate("/login");
  // };

  // if (!isAuthenticated) {
  //   return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  // }

  const appRoutes = (
    <>
      <SessionTimeout app="discipline" />
    <Routes>
      <Route path="/" element={<DashboardPage />} />
      <Route path="/user-register" element={<UserRegister />} />
      <Route path="/complaint-request" element={<ComplaintRequest />} />
      <Route path="/complaint-request/:uu_id" element={<SingleComplaint />} />
      <Route path="/new-complaint" element={<NewComplaint />} />

      <Route path="/appeal-request" element={<AppealRequest />} />
      <Route path="/appeal-request/:uu_id" element={<SingleAppeal />} />

      <Route path="/accused-complaint/" element={<AccusedRequests />} />
      <Route path="/accused-complaint/:uu_id" element={<SingleAccused />} />

      <Route path="/court-appeal/" element={<CourtAppealRequests />} />
      <Route path="/court-appeal/:uu_id" element={<SingleCourtAppeal />} />

      <Route path="/advocate-complaint" element={<AdvocateComplaint />} />
      <Route
        path="/advocate-complaint/new"
        element={<NewAdvocateComplaint />}
      />
      <Route
        path="/advocate-complaint/:uu_id"
        element={<SingleAdvocateComplaint />}
      />

      <Route path="/advocate-appeal" element={<AdvocateAppealRequest />} />
      <Route
        path="/advocate-appeal/:uu_id"
        element={<SingleAdvocateAppeal />}
      />

      <Route
        path="/advocate-accused-appeal"
        element={<AdvocateAccusedRequests />}
      />
      <Route
        path="/advocate-accused-appeal/:uu_id"
        element={<SingleAdvocateAccused />}
      />

      <Route
        path="/advocate-court-appeal"
        element={<AdvocateCourtAppealRequests />}
      />
      <Route
        path="/advocate-court-appeal/:uu_id"
        element={<SingleAdvocateCourtAppeal />}
      />

      <Route
        path="/activity-logs/prosecutor"
        element={<ListProsecutorLogs />}
      />
      <Route path="/activity-logs/advocate" element={<ListAdvocateLogs />} />
      <Route path="/report-by-mobile" element={<ReportByMobileRequests />} />
      <Route path="/report-by-mobile/:id" element={<SingleMobileReuqest />} />
    </Routes>
    </>
  );

  return !internalUserType ? (
    // External User Layout - Fixed Header on top, Fixed Sidebar below header
    <div className="h-screen bg-gray-100">
      {/* Fixed Header */}
      <Header isInternalUser={false} />

      {/* Fixed Sidebar */}
      <div className="hidden md:block">
        <Sidebar isInternalUser={false} />
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          ></div>
          <div className="fixed inset-y-0 left-0 z-50 w-64">
            <Sidebar isInternalUser={false} />
          </div>
        </div>
      )}

      {/* Main Content - positioned to account for fixed header and sidebar */}
      <main className="relative z-10 pt-16 md:pt-20 md:pl-72 overflow-y-auto h-full p-4 md:p-6">
        {/* Mobile Menu Button */}
        <div className="md:hidden mb-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="mobile-menu-btn"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
        {appRoutes}
      </main>
    </div>
  ) : (
    // Internal User Layout - Fixed Sidebar on left, Fixed Header on right
    <div className="h-screen flex bg-gray-100">
      {/* Fixed Sidebar */}
      <div className="hidden md:block">
        <Sidebar isInternalUser={true} />
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          ></div>
          <div className="fixed inset-y-0 left-0 z-50 w-64">
            <Sidebar isInternalUser={true} />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Fixed Header */}
        <Header isInternalUser={true} />

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Mobile Menu Button */}
          <div className="md:hidden mb-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="mobile-menu-btn"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
          {appRoutes}
        </main>
      </div>
    </div>
  );
}
